import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router-dom";

import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import { sendTelemetryEvent } from "@/lib/telemetry";
import type { IShare } from "@/src/common/ShareDefinitions";
import { useViewportInfoContext } from "@/src/contexts";
import { useSearchNavigation } from "@/src/contexts/useSearchNavigation";
import type { IFlyerCatalogueResources } from "@/src/flyers/FlyerCatalogue/FlyerCatalogue.Resources";
import {
    FlyerDisplayPosition,
    type IFlyerCatalogueImage,
} from "@/src/flyers/FlyerCatalogue/FlyerCatalogueHelpers";
import FlyerCatalogueModalShare from "@/src/flyers/FlyerCatalogue/FlyerCatalogueModalShare";
import { useFlyerDetailsContext } from "@/src/flyers/FlyerDetails/FlyerDetailsContext";
import Container from "@Components/Container";
import * as modalCss from "@Components/Modal/Modal.module.css";
import { FlyerEventType, TelemetryEntity, getHeaderHeight } from "@Components/Modules/Helpers";
import { flyerPageQueryParam, mainHeaderId } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { ArrowLeft } from "../../../design-system/icons/arrow-left";
import { type QueryXsMax, colors } from "../../../styles/tokens.stylex";
import FlyersCatalogueCarousel from "./FlyersCatalogueCarousel";

export type IFlyerCatalogueModal = {
    magazine: IFlyerCatalogueImage[];
    flyerId: string;
    brandName?: string;
    brandUrlSeo: string;
    validityTitle?: string;
    showNavigationArrows?: boolean;
    pageNavigationFormat?: string;
    hideNavigation?: boolean;
    share?: IShare;
    resources: IFlyerCatalogueResources;
    onGoBack: () => boolean;
    onModalClose: () => void;
    title: string;
    caption: string;
    shareUrl: string;
    totalPages: number;
    magazineIndex: number;
};

export default function FlyerCatalogueModal(props: IFlyerCatalogueModal) {
    const [headerHeight, setHeaderHeight] = useState(0);
    const showSearchNavigation = useSearchNavigation();
    const { isMobileOrTablet } = useViewportInfoContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isZoomed } = useFlyerDetailsContext();
    const { pathname } = useLocation();
    const [isClient, setIsClient] = useState(false);

    const [activePage, setActivePage] = useState(0);

    const timeoutRef = useRef(0);

    const onPageVisible = (page: number) => {
        setActivePage(page);
        const p = props.magazine[page]?.pageNumber;

        if (p && p !== Number(searchParams.get(flyerPageQueryParam))) {
            clearTimeout(timeoutRef.current);
            searchParams.set(flyerPageQueryParam, `${p}`);
            timeoutRef.current = window.setTimeout(setSearchParams, 300, searchParams, { replace: true });
        }
    };

    const flyerPageRange = useMemo(() => {
        const page = props.magazine[activePage];

        if (page.isBanner) {
            return null;
        }

        let pageText = `${page.pageNumber}`;

        if (page.displayPosition !== FlyerDisplayPosition.Center && !isMobileOrTablet && !isZoomed) {
            if (page.displayPosition === FlyerDisplayPosition.Left) {
                pageText = `${page.pageNumber}-${page.pageNumber + 1}`;
            } else {
                pageText = `${page.pageNumber - 1}-${page.pageNumber}`;
            }
        }

        const rangeText = props.resources.paginationText
            .replace("{page}", pageText)
            .replace("{totalPages}", props.totalPages.toString());

        return rangeText;
    }, [activePage, isZoomed, isMobileOrTablet, pathname]);

    const onSlidePage = () => {
        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Flyer}-${FlyerEventType.pageBrowse}`,
            actionType: "Click",
            itemId: props.flyerId,
        });
    };

    useEffect(() => {
        setIsClient(true);
        const updateHeight = () => {
            const cHeaderHeight = getHeaderHeight();
            if (headerHeight !== cHeaderHeight) {
                setHeaderHeight(cHeaderHeight);
            }
        };

        const closeModalOnEsc = (event: KeyboardEvent): void => {
            if (event.key === "Escape" && props.onModalClose) props.onModalClose();
        };

        document.addEventListener("keydown", closeModalOnEsc);
        document.body.classList.add(modalCss.modalOpen);

        const urlData = new URL(window.location.href);

        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Flyer}-${FlyerEventType.flyerPagesClick}`,
            actionType: "Undefined",
            itemId: props.flyerId,
            param1: urlData.searchParams?.get("navigationKey") ? undefined : "FlyerDetails",
        });

        const resizeObserver = new ResizeObserver(() => {
            updateHeight();
        });

        const headerElement = document.getElementById(mainHeaderId);
        resizeObserver.observe(headerElement);

        return () => {
            document.removeEventListener("keydown", closeModalOnEsc);
            document.body.classList.remove(modalCss.modalOpen);
            resizeObserver.unobserve(headerElement);
        };
    }, []);

    if (!isClient) {
        return null;
    }

    const navigation = props.hideNavigation ? null : (
        <div {...stylex.props(styles.header)}>
            <Container styleX={[styles.pageInformation, styles.containerMargin, styles.containerPadding]}>
                <div {...stylex.props(styles.textIcon)}>
                    <button
                        type="button"
                        aria-label="go back"
                        className={stylex.props(styles.arrowBack).className}
                        onClick={props.onGoBack}
                    >
                        <ArrowLeft />
                    </button>

                    <CloseIconSlimButton
                        styleX={styles.textIconButton}
                        aria-label="close"
                        onClick={props.onModalClose}
                        color="#fff"
                    />

                    <div>
                        <span {...stylex.props(styles.description)}>{props.brandName}</span>
                        <span
                            {...stylex.props(styles.validDate)}
                            dangerouslySetInnerHTML={{ __html: props.validityTitle || "" }}
                        />
                    </div>
                </div>
                <span {...stylex.props(styles.pageNavigation)}>{flyerPageRange}</span>
                <FlyerCatalogueModalShare {...props} />
            </Container>
        </div>
    );

    if (showSearchNavigation) {
        return createPortal(
            <div {...stylex.props(styles.pageModal(headerHeight))}>
                <Container styleX={[styles.carouselContainer, styles.carouselContainerSearch]}>
                    <FlyersCatalogueCarousel
                        onMobileSlide={onSlidePage}
                        onPrevClick={onSlidePage}
                        onNextClick={onSlidePage}
                        pages={props.magazine}
                        flyerId={props.flyerId}
                        onVisible={onPageVisible}
                        hideArrows
                    />
                </Container>
            </div>,
            document.getElementById(mainHeaderId),
        );
    }

    return createPortal(
        <div {...stylex.props(styles.pageModal(0))}>
            {navigation}
            <Container styleX={[styles.carouselContainer]}>
                <FlyersCatalogueCarousel
                    onMobileSlide={onSlidePage}
                    onPrevClick={onSlidePage}
                    onNextClick={onSlidePage}
                    pages={props.magazine}
                    flyerId={props.flyerId}
                    onVisible={onPageVisible}
                />
            </Container>
        </div>,
        document.body,
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const modalHeight = "75px";

const styles = stylex.create({
    pageInformation: {
        gridTemplateAreas: "'iconText pag actionLink'",
        alignItems: "center",
        display: "grid",
        gridTemplateColumns: "1fr 1fr auto",
    },

    pageNavigation: {
        gridColumnEnd: "pag",
        gridColumnStart: "pag",
        gridRowEnd: "pag",
        gridRowStart: "pag",
        textAlign: "center",
    },

    textIcon: {
        gap: 20,
        alignItems: "center",
        display: "flex",
        gridColumnEnd: "iconText",
        gridColumnStart: "iconText",
        gridRowEnd: "iconText",
        gridRowStart: "iconText",
    },

    textIconButton: {
        color: colors.bg_Primary,
        opacity: {
            default: null,
            "::hover": "unset",
        },
        height: 22,
        width: 22,
    },

    arrowBack: {
        color: colors.bg_Primary,
        height: 35,
        width: 35,
    },

    pageModal: (height: number) => ({
        backgroundColor: "#000000cc",

        position: "fixed",

        zIndex: height > 0 ? -1 : 60,
        height: `calc(100vh - ${height}px)`,
        left: 0,
        right: 0,
        top: height,
    }),

    containerMargin: {
        padding: 0,
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
    },

    containerPadding: {
        padding: {
            default: "10px 0",
            [xsMax]: "0 10px",
        },
    },

    carouselContainer: {
        margin: {
            default: "0 auto",
            [xsMax]: 0,
        },
        padding: {
            default: "10px 0",
            [xsMax]: 0,
        },
        height: `calc(100% - ${modalHeight})`,
    },

    carouselContainerSearch: {
        height: "100%",
    },

    header: {
        backgroundColor: "black",
        color: colors.bg_Primary,
        display: "block",
        fontSize: "0.875rem",
        userSelect: "none",
        zIndex: 1,
        height: modalHeight,
        width: "100%",
    },

    description: {
        display: "block",
        fontSize: "1.125rem",
        lineHeight: 1.22,
    },

    validDate: {
        display: "block",
        fontSize: "0.75rem",
        opacity: 0.6,
    },
});
